import { useEffect, useMemo, useState } from "react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtTL, MONTHS_TR, BILLING_METHODS, onetimeStatusMeta, fmtDateTR } from "@/lib/finance";
import { BellRing, Check, Store, ChevronDown, ChevronRight, Trash2 } from "lucide-react";

const monthLbl = (y, m) => `${MONTHS_TR[m - 1]} ${y}`;

export default function PaymentsDue() {
  const { workspace } = useWorkspace();
  const [data, setData] = useState({ recurring: [], onetime: [], total_debt: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedRec, setSelectedRec] = useState({}); // { rowId: {amount, method} }
  const [selectedOt, setSelectedOt] = useState({}); // { jobId: {amount, method} }
  const [expanded, setExpanded] = useState({}); // firm brand expanded state
  const [tab, setTab] = useState("all"); // all | recurring | onetime

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/finance/payments-due", { params: { workspace } });
      setData(d);
      // auto-expand all firms initially
      const exp = {};
      (d.recurring || []).forEach((f) => { exp[f.brand_name] = true; });
      setExpanded(exp);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Yüklenemedi"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);

  const totalSelected = useMemo(() => {
    let s = 0;
    Object.values(selectedRec).forEach((v) => { if (v?.checked) s += Number(v.amount || 0); });
    Object.values(selectedOt).forEach((v) => { if (v?.checked) s += Number(v.amount || 0); });
    return s;
  }, [selectedRec, selectedOt]);

  const submit = async () => {
    const items = [];
    Object.entries(selectedRec).forEach(([id, v]) => {
      if (v?.checked && Number(v.amount || 0) > 0) {
        items.push({ type: "recurring", id, amount: Number(v.amount), method: v.method || null });
      }
    });
    Object.entries(selectedOt).forEach(([id, v]) => {
      if (v?.checked && Number(v.amount || 0) > 0) {
        items.push({ type: "onetime", id, amount: Number(v.amount), method: v.method || null });
      }
    });
    if (!items.length) {
      toast.error("En az bir kalem seçin ve tutar girin");
      return;
    }
    try {
      const { data: r } = await api.post("/finance/payments-due/settle", { workspace, items });
      toast.success(`${r.count} ödeme kaydedildi`);
      setSelectedRec({}); setSelectedOt({});
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Kaydedilemedi"));
    }
  };

  const setRec = (id, patch) => setSelectedRec((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
  const setOt = (id, patch) => setSelectedOt((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const deleteOnetime = async (o) => {
    if (!window.confirm(`${o.brand_name} tek seferlik işi silinsin mi? İş iptal edilmiş sayılacak ve cirodan düşülecek.`)) return;
    try {
      await api.delete(`/finance/onetime-jobs/${o.id}`);
      toast.success("Tek seferlik iş silindi");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Silinemedi")); }
  };

  const filterRec = tab === "onetime" ? [] : data.recurring;
  const filterOt = tab === "recurring" ? [] : data.onetime;

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <BellRing size={28} className="text-emerald-700" /> Ödeme Bildirimi
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Ödenmemiş faturalar ve tek seferlik işlerdeki açık bakiyeler. Kutucuğu işaretleyin, alınan tutarı girin ve kaydedin. Cari aya ciro olarak yansır.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Toplam Açık Bakiye</p>
          <p className="text-2xl font-bold text-red-600" data-testid="pd-total-debt">{fmtTL(data.total_debt || 0)}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {[["all", "Tümü"], ["recurring", "Sürekli Borçlar"], ["onetime", "Tek Seferlik"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`pd-tab-${id}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? <div className="text-slate-500 text-sm">Yükleniyor…</div> : (
        <div className="space-y-8">
          {/* Recurring (past months) */}
          {filterRec.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Sürekli İşler — Vadesi Gelmiş / Geçmiş Borçlar</h2>
              <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
                {filterRec.map((firm) => (
                  <div key={firm.brand_name} className="border-b border-slate-100 last:border-none" data-testid={`pd-firm-${firm.brand_name}`}>
                    <button onClick={() => setExpanded((s) => ({ ...s, [firm.brand_name]: !s[firm.brand_name] }))}
                      className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        {expanded[firm.brand_name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <Store size={14} className="text-slate-400" />
                        <span className="text-sm font-semibold text-slate-900">{firm.brand_name}</span>
                        <span className="text-xs text-slate-400">({firm.months.length} dönem)</span>
                      </div>
                      <span className="text-sm font-semibold text-red-600">{fmtTL(firm.total_debt)}</span>
                    </button>
                    {expanded[firm.brand_name] && (
                      <div className="border-t border-slate-100 bg-slate-50/30">
                        {firm.months.map((m) => {
                          const s = selectedRec[m.id] || {};
                          return (
                            <div key={m.id} className="grid grid-cols-[36px_1fr_150px_150px_170px] gap-3 items-center px-5 py-3 border-b border-slate-100 last:border-none" data-testid={`pd-rec-${m.id}`}>
                              <input type="checkbox" checked={!!s.checked} onChange={(e) => setRec(m.id, { checked: e.target.checked, amount: s.amount ?? m.remaining })} data-testid={`pd-rec-chk-${m.id}`} className="w-4 h-4 accent-emerald-700" />
                              <div>
                                <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                                  {monthLbl(m.year, m.month)}
                                  {m.is_current_month && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`pd-rec-overdue-${m.id}`}>Vadesi Geldi</span>}
                                </div>
                                <div className="text-xs text-slate-500">Tutar: {fmtTL(m.amount)} · Ödenen: {fmtTL(m.partial_amount)} · Kalan: <span className="text-red-600 font-medium">{fmtTL(m.remaining)}</span></div>
                              </div>
                              <input type="number" step="0.01" placeholder="Ödenen ₺" value={s.amount ?? ""} onChange={(e) => setRec(m.id, { amount: e.target.value, checked: true })} data-testid={`pd-rec-amt-${m.id}`}
                                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100" />
                              <select value={s.method || ""} onChange={(e) => setRec(m.id, { method: e.target.value })} data-testid={`pd-rec-mth-${m.id}`}
                                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
                                <option value="">Yöntem seç…</option>
                                {BILLING_METHODS.map((mo) => <option key={mo.value} value={mo.value}>{mo.label}</option>)}
                              </select>
                              <button onClick={() => setRec(m.id, { amount: m.remaining, checked: true })} className="text-xs text-emerald-700 hover:underline" data-testid={`pd-rec-full-${m.id}`}>Tümünü ödendi işaretle</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* One-time */}
          {filterOt.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Tek Seferlik İşler — Açık Bakiyeler</h2>
              <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
                {filterOt.map((o) => {
                  const s = selectedOt[o.id] || {};
                  const stMeta = onetimeStatusMeta(o.status);
                  return (
                    <div key={o.id} className="grid grid-cols-[36px_1fr_150px_150px_40px] gap-3 items-center px-5 py-4 border-b border-slate-100 last:border-none" data-testid={`pd-ot-${o.id}`}>
                      <input type="checkbox" checked={!!s.checked} onChange={(e) => setOt(o.id, { checked: e.target.checked, amount: s.amount ?? o.remaining })} data-testid={`pd-ot-chk-${o.id}`} className="w-4 h-4 accent-emerald-700" />
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{o.brand_name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          <span>{fmtDateTR(o.date)}</span>
                          <span>·</span>
                          <span>{stMeta?.label || o.status}</span>
                          <span>·</span>
                          <span>Anlaşma: {fmtTL(o.amount)}</span>
                          <span>·</span>
                          <span>Alınan: {fmtTL(o.partial_payment)}</span>
                          <span>·</span>
                          <span className="text-red-600 font-medium">Kalan: {fmtTL(o.remaining)}</span>
                        </div>
                      </div>
                      <input type="number" step="0.01" placeholder="Ödenen ₺" value={s.amount ?? ""} onChange={(e) => setOt(o.id, { amount: e.target.value, checked: true })} data-testid={`pd-ot-amt-${o.id}`}
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100" />
                      <select value={s.method || ""} onChange={(e) => setOt(o.id, { method: e.target.value })} data-testid={`pd-ot-mth-${o.id}`}
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
                        <option value="">Yöntem seç…</option>
                        {BILLING_METHODS.map((mo) => <option key={mo.value} value={mo.value}>{mo.label}</option>)}
                      </select>
                      <button onClick={() => deleteOnetime(o)} data-testid={`pd-ot-del-${o.id}`} title="İşi sil (iptal)"
                        className="w-8 h-8 rounded-md hover:bg-red-50 text-red-500 hover:text-red-600 inline-flex items-center justify-center">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {(!filterRec.length && !filterOt.length) && (
            <div className="rounded-2xl border border-slate-100 bg-white p-10 text-center" data-testid="pd-empty">
              <p className="text-slate-500 text-sm">Bu görünümde açık borç yok.</p>
            </div>
          )}
        </div>
      )}

      {/* Sticky save bar */}
      <div className="sticky bottom-6 mt-8 flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-lg">
        <p className="text-sm text-slate-600">Seçilen toplam ödeme: <span className="font-semibold text-emerald-700 text-base" data-testid="pd-selected-total">{fmtTL(totalSelected)}</span></p>
        <button onClick={submit} data-testid="pd-save"
          className="inline-flex items-center gap-2 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
          disabled={totalSelected <= 0}>
          <Check size={14} /> Kaydet
        </button>
      </div>
    </FinanceLayout>
  );
}
